import {Button, Checkbox, Pagination, Tag} from "omoplay";
import {useEffect, useMemo, useState} from "react";
import {getSpotsAllPages, getSpotIdsByTag, getSpotTags} from "../../../api/spot/spot.api";
import {getEntryPointOccupancy} from "../../../api/project/project.api";
import SearchInput from "../../SearchInput";
import {matchesSpotKeyword, spotShortId, spotTypeLabel} from "../../../libs/spot";

const PAGE_SIZE = 50

/**
 * 프로젝트 이벤트에 연결할 진입점 선택 팝업.
 *
 * 🔴 omoplay `Table` 의 checkbox 를 쓰지 않는다. 그쪽 `checkedList` 는 컴포넌트 내부 state 이고
 *    외부에서 제어할 prop 이 없어서 ⓐ "검색 결과 전체 선택" 을 화면에 반영할 수 없고
 *    ⓑ 전체선택 시 시각적으로는 모든 페이지가 체크되는데 콜백은 현재 페이지만 넘겨 서로 어긋난다.
 *    그래서 제어형 `Checkbox` 로 직접 구성하고, 선택 상태는 **행 인덱스가 아니라 id 집합**으로 들고 있다.
 *
 * 🔴 이미 연결된 진입점을 **목록에서 빼지 않는다**(2026-08-27 사용자 피드백).
 *    종전에는 제외해서 "609건이 빠짐없이 들어갔는지" 확인할 방법이 없었고, 담당자는 매번
 *    처음부터 다시 넣어야 하는 것처럼 느꼈다. 이제 **'연결됨'으로 체크된 상태로 보여준다.**
 *
 * 🔴 `allowDisconnect` = **이 팝업에서 연결 해제까지 한다**(2026-09-03 사용자 요청).
 *    종전에는 체크를 고정해 두고 "해제는 프로젝트 상세의 '전체 보기 · 관리' 팝업" 으로 미뤘는데,
 *    사용자 지적 = **"추가는 팝업에서 하고 빼는 건 나가서 목록에서 해야 하는 것처럼 보인다."**
 *    ⇒ 체크를 풀면 이미 연결된 진입점의 연결이 해제된다.
 *    🔧 2026-09-22 정정 — 종전에는 이 값이 `project.disabledDelete === false`(아직 한 번도 시작/종료하지
 *       않은 프로젝트)였다. 운영 중인 프로젝트는 해제 경로가 아예 없어 DB 를 직접 고쳐야 했고,
 *       추가는 열려 있어 비대칭이었다. 지금은 **항상 허용**이며 위험한 경우(이벤트의 마지막 진입점)는
 *       서버가 거부한다. 분석 = `_ai-runs/2026-09-22_005_started_project_entrypoint_unlink/001_analysis.md`
 *    추가분은 `onSelectionChange`, 해제분은 `onRemovalChange` 로 따로 올려보낸다
 *    (한 배열로 합치면 부모가 "무엇을 POST 하고 무엇을 DELETE 할지" 를 다시 계산해야 한다).
 */
export const EntrypointSimpleList = ({selectedEntrypointIds, isSingle = false, allowDisconnect = false,
                                      visible = true, initialConnectedOnly = false, occupancyExcludeProjectId,
                                      onSelected, onSelectionChange, onRemovalChange}) => {
    const [entrypointList, setEntrypointList] = useState([])
    const [loading, setLoading] = useState(true)
    const [keyword, setKeyword] = useState("")
    const [page, setPage] = useState(1)
    // 새로 선택한 것만 담는다(이미 연결된 것은 connectedIds 로 따로 본다).
    const [selectedIds, setSelectedIds] = useState(() => new Set())
    // 이미 연결된 것 중 **해제하기로 표시한** 것. `allowDisconnect` 일 때만 채워진다.
    const [removedIds, setRemovedIds] = useState(() => new Set())
    /* '연결된 것만 보기' 필터. 프로젝트 상세의 '전체 보기 · 관리' 로 열면 이 상태로 시작한다
       (종전의 별도 팝업 `ConnectedEntrypointsPopup` 이 하던 일 = 연결된 것만 보여주기). */
    const [connectedOnly, setConnectedOnly] = useState(initialConnectedOnly)

    const connectedIds = useMemo(() => new Set(selectedEntrypointIds ?? []), [selectedEntrypointIds])
    // 진입점 그룹(해시태그) — 여기가 태그의 가장 큰 값이다. 609건을 두 번 클릭으로 고른다.
    const [tags, setTags] = useState([])
    const [tagBusy, setTagBusy] = useState(null)

    useEffect(() => {
        let alive = true
        setLoading(true)
        // 채널 전체를 받아 클라이언트에서 검색·페이징한다(서버 /spot 에 검색 파라미터가 없다).
        getSpotsAllPages().then(({data}) => {
            if (!alive) return
            setEntrypointList(data.qrSpots ?? [])
            setLoading(false)
        }).catch(() => {
            if (!alive) return
            setEntrypointList([])
            setLoading(false)
        })
        return () => { alive = false }
    }, [])

    useEffect(() => {
        getSpotTags().then(({data}) => setTags(data ?? [])).catch(() => setTags([]))
    }, [])

    /* 진입점별 점유 현황(다른 **진행중·예약** 프로젝트가 쓰고 있는지).
       🔴 고르는 것 자체는 막지 않는다(다음 프로젝트를 미리 준비하는 운영을 막지 않는다).
          대신 **고르는 순간에 보여줘야** 한다 — 못 보면 나중에 시작 버튼에서야 막히고,
          진입점 600개 화면에서는 무엇이 걸렸는지 되짚기가 사실상 불가능하다.
       🔧 실패하면 조용히 빈 맵으로 둔다. 이 정보가 없다고 선택 자체를 막을 이유는 없다. */
    const [occupancy, setOccupancy] = useState({})
    useEffect(() => {
        if (isSingle) return
        getEntryPointOccupancy(occupancyExcludeProjectId)
            .then(({data}) => setOccupancy(data ?? {}))
            .catch(() => setOccupancy({}))
    }, [isSingle, occupancyExcludeProjectId])

    /* 🔴 팝업이 닫히면 검색·선택·해제·필터를 초기화한다.
       omoplay `Modal` 은 `Dialog.Content` 를 **`forceMount`** 로 띄우므로 닫아도 내용이 **언마운트되지 않는다.**
       초기화하지 않으면 다시 열었을 때 지난 체크가 남아 있는데 부모의 확정 목록은 비어 있어
       **화면과 버튼 라벨이 어긋난다**(같은 함정을 `ConnectedEntrypointsPopup` 이 먼저 겪었다).
       진입점 목록(`entrypointList`) 자체는 그대로 둔다 — 매번 다시 받을 이유가 없다. */
    useEffect(() => {
        if (visible) {
            setConnectedOnly(initialConnectedOnly)   // 어느 버튼으로 열었는지에 따라 필터가 달라진다
            return
        }
        setKeyword("")
        setPage(1)
        setSelectedIds(new Set())
        setRemovedIds(new Set())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, initialConnectedOnly])

    const filtered = useMemo(
        () => entrypointList.filter((spot) =>
            matchesSpotKeyword(spot, keyword) && (connectedOnly === false || connectedIds.has(spot.id))),
        [entrypointList, keyword, connectedOnly, connectedIds]
    )
    const filteredCount = filtered.length

    /** 검색 결과 중 **아직 연결되지 않은** 것 = 실제로 선택 가능한 대상. */
    const selectable = useMemo(
        () => filtered.filter((spot) => !connectedIds.has(spot.id)),
        [filtered, connectedIds]
    )
    const selectableCount = selectable.length

    const paged = useMemo(
        () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filtered, page]
    )

    // 검색으로 결과가 줄어 현재 페이지가 범위를 넘으면 빈 화면이 된다 → 마지막 페이지로 당긴다.
    useEffect(() => {
        const lastPage = Math.max(1, Math.ceil(filteredCount / PAGE_SIZE))
        if (page > lastPage) {
            setPage(lastPage)
        }
    }, [filteredCount, page])

    /** 확정 후 연결된 상태가 되는가 = 체크박스의 표시 상태. */
    const isChecked = (spot) => connectedIds.has(spot.id) ? !removedIds.has(spot.id) : selectedIds.has(spot.id)
    /** 체크를 바꿀 수 있는 대상. 해제를 허용하면 이미 연결된 것도 포함된다. */
    const pagedTargets = allowDisconnect ? paged : paged.filter((spot) => !connectedIds.has(spot.id))
    const pagedAllChecked = pagedTargets.length > 0 && pagedTargets.every(isChecked)

    /** 주어진 진입점들을 한 번에 체크/해제한다. 페이지를 넘어도 누적된다.
     *  이미 연결된 것은 `removedIds`, 아직 아닌 것은 `selectedIds` 로 갈라 담는다. */
    const toggleMany = (spots, checked) => {
        const connected = spots.filter((spot) => connectedIds.has(spot.id))
        const fresh = spots.filter((spot) => !connectedIds.has(spot.id))
        if (fresh.length > 0) {
            setSelectedIds((prev) => {
                const next = new Set(prev)
                fresh.forEach((spot) => { if (checked) next.add(spot.id); else next.delete(spot.id) })
                return next
            })
        }
        // 해제를 허용하지 않으면 이미 연결된 것은 건드리지 않는다(체크 고정).
        if (allowDisconnect && connected.length > 0) {
            setRemovedIds((prev) => {
                const next = new Set(prev)
                connected.forEach((spot) => { if (checked) next.delete(spot.id); else next.add(spot.id) })
                return next
            })
        }
    }

    /**
     * 그룹에 속한 진입점을 한 번에 선택한다.
     * 이미 연결된 것은 자동으로 빠지므로(toggleMany 가 걸러낸다) "남은 것만" 선택된다.
     */
    const selectByTag = async (tagCode) => {
        setTagBusy(tagCode)
        try {
            const {data} = await getSpotIdsByTag(tagCode)
            const ids = new Set(data ?? [])
            toggleMany(entrypointList.filter((spot) => ids.has(spot.id)), true)
        } finally {
            setTagBusy(null)
        }
    }

    const selectedCount = selectedIds.size
    const removedCount = removedIds.size
    /** 새로 고른 것 중 다른 진행중·예약 프로젝트가 점유한 건수(= 시작 시점에 막힐 후보). */
    const occupiedSelectedCount = [...selectedIds].filter((id) => (occupancy[id] ?? []).length > 0).length
    const connectedInFilter = filteredCount - selectableCount

    // 확정 버튼은 팝업 **푸터**에 있다(모달의 '확인'). 선택이 바뀔 때마다 실제 객체 목록을 올려보낸다.
    // 종전에는 내용 안에 '선택 연결' 버튼이 따로 있고 푸터는 '닫기' 하나가 자리를 차지했다.
    useEffect(() => {
        if (!onSelectionChange) return
        onSelectionChange(entrypointList.filter((spot) => selectedIds.has(spot.id)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds, entrypointList])

    // 해제분은 따로 올려보낸다 → 부모가 POST(추가)와 DELETE(해제)를 그대로 나눠 호출한다.
    useEffect(() => {
        if (!onRemovalChange) return
        onRemovalChange(entrypointList.filter((spot) => removedIds.has(spot.id)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [removedIds, entrypointList])

    return (
      /* `modal-fit-fill` = 팝업(85vh)에서 헤더·푸터를 뺀 남은 높이를 이 루트가 받는다.
         그 안에서 목록(`.modal-scroll-area`)만 스크롤하고 검색창·그룹·버튼·페이지네이션은 자리에 남는다.
         🔴 종전에는 목록에 `calc(85vh - 360px)` 추정 상한을 줬는데, 그룹 칩 줄이 접히면 위쪽 UI 높이가
            바뀌므로 맞을 수가 없었다 → 페이지네이션이 잘리고 **모달 외부 스크롤까지 두 겹**이 됐다.
            자세한 계산·후퇴 경로는 `assets/common.css` 의 `.modal-fit` 높이 사슬 주석 참조.
         marginTop 24 → 8: 타이틀과 검색창 사이가 넓어 보였다(modal-content 에 이미 패딩 16px 이 있다). */
      <div className="modal-fit-fill flex-col gap-4" style={{marginTop: "8px"}}>
          <div className="flex gap-4 items-center justify-between flex-shrink-0">
              <div className="max-w-[405px] w-[405px] flex-shrink-0">
                  <SearchInput
                      instant={true}
                      placeholder="진입점명, ID, 타입으로 검색"
                      onSubmit={(text) => {
                          setPage(1) // 검색어가 바뀌면 항상 1페이지부터 본다
                          setKeyword(text)
                      }}
                  />
              </div>
              {
                  /* '연결된 것만 보기' = 종전 '연결된 진입점 관리' 팝업의 역할.
                     팝업을 하나로 합쳤으므로(2026-09-03 사용자 요청 "두 팝업이 기본적으로 동일해야 한다")
                     여기서 켜고 끈다. 끄면 그대로 '추가하기' 화면이 된다. */
                  isSingle === false && (
                      <Button variants={connectedOnly ? "dark" : "default"}
                              value={connectedOnly ? "연결된 것만 보기 ✓" : "연결된 것만 보기"}
                              onClick={() => { setPage(1); setConnectedOnly((prev) => !prev) }}
                      />
                  )
              }
              <div className="text-sm text-gray-500 whitespace-nowrap">
                  {connectedOnly
                      ? `연결된 진입점 ${filteredCount}건${keyword.trim() === "" ? "" : "(검색 결과)"} / 전체 ${entrypointList.length}개`
                      : keyword.trim() === ""
                          ? `전체 ${entrypointList.length}개 · 이미 연결 ${connectedIds.size}개`
                          : `검색 결과 ${filteredCount}개 (이미 연결 ${connectedInFilter}개) / 전체 ${entrypointList.length}개`}
              </div>
          </div>

          {
              isSingle === false && tags.length > 0 && (
                  <div className="flex gap-2 items-center flex-wrap">
                      <div className="text-sm font-semibold whitespace-nowrap">그룹으로 선택</div>
                      {
                          tags.map((t) => (
                              <Button key={t.tagCode} variants="default"
                                      disabled={tagBusy != null}
                                      value={tagBusy === t.tagCode ? "..." : `# ${t.tagCode} (${t.spotCount})`}
                                      onClick={() => selectByTag(t.tagCode)}
                              />
                          ))
                      }
                  </div>
              )
          }

          {
              isSingle === false && (
                  <div className="flex gap-4 items-center justify-between">
                      <div className="flex gap-2 items-center">
                          {/* 🔴 이 화면의 핵심. 600개가 넘는 진입점을 페이지마다 고르는 것은 불가능하다.
                              이미 연결된 것은 대상에서 빠지므로 '남은 것만' 선택된다. */}
                          <Button variants="default"
                                  disabled={selectableCount === 0}
                                  value={keyword.trim() === ""
                                      ? `연결 안 된 전체 선택 (${selectableCount}건)`
                                      : `검색 결과 전체 선택 (${selectableCount}건)`}
                                  onClick={() => toggleMany(selectable, true)}
                          />
                          <Button variants="default"
                                  disabled={selectedCount === 0 && removedCount === 0}
                                  value={allowDisconnect ? "변경 초기화" : "선택 초기화"}
                                  onClick={() => { setSelectedIds(new Set()); setRemovedIds(new Set()) }}
                          />
                      </div>
                      {/* 확정은 팝업 푸터의 '확인' 이 담당한다 → 여기서는 건수만 보여준다 */}
                      <div className="text-sm font-semibold whitespace-nowrap">
                          새로 선택 {selectedCount}건
                          {removedCount > 0 && <span className="text-red-600"> · 해제 {removedCount}건</span>}
                          {/* 고른 것 중 다른 프로젝트가 점유 중인 건수 — 시작 시점에 막힐 후보다 */}
                          {occupiedSelectedCount > 0 &&
                              <span className="text-amber-700"> · 점유 중 {occupiedSelectedCount}건</span>}
                      </div>
                  </div>
              )
          }

          {
              isSingle === false && allowDisconnect && (
                  <div className="text-sm text-gray-500">
                      체크를 풀면 <b>이미 연결된 진입점의 연결이 해제</b>됩니다. 확정은 아래 <b>연결</b> 버튼입니다.
                  </div>
              )
          }

          {
              /* 점유 안내 — 막지 않는 대신 무엇이 걸릴지 미리 알려준다. */
              isSingle === false && Object.keys(occupancy).length > 0 && (
                  <div className="text-sm text-amber-700">
                      <b>점유 중</b> = 다른 진행중·예약 프로젝트가 쓰고 있는 진입점입니다.
                      지금 고를 수는 있지만, <b>기간이 겹치면 프로젝트를 시작/예약할 때 막힙니다.</b>
                  </div>
              )
          }

          {
              isSingle === false && selectableCount === 0 && filteredCount > 0 && (
                  <div className="text-sm text-gray-500">
                      {keyword.trim() === ""
                          ? "이 채널의 진입점이 모두 연결되어 있습니다."
                          : "검색 결과가 모두 연결되어 있습니다."}
                  </div>
              )
          }

          {/* 🔴 여기서만 스크롤한다. 위의 검색창·버튼과 아래 페이지네이션은 화면에 남는다.
              🔴 `scrollbar-visible` 이 없으면 스크롤은 되는데 스크롤바가 안 보인다 —
                 전역 `::-webkit-scrollbar { display:none }` 때문(common.css ≈659행). */}
          <div className="modal-scroll-area scrollbar-visible flex-col border-t border-gray-200">
              {/* 표 헤더 — 스크롤 영역 안에서 sticky 로 붙여 스크롤해도 보이게 한다 */}
              <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-600 bg-white sticky top-0 z-10">
                  {
                      isSingle === false && (
                          <div className="w-6 flex-shrink-0">
                              {/* 현재 페이지만 토글. 다른 페이지의 선택·해제는 건드리지 않는다.
                                  해제를 허용하면 이미 연결된 행까지 대상이라 이 체크박스로 **페이지 일괄 해제**가 된다. */}
                              <Checkbox checked={pagedAllChecked}
                                        disabled={pagedTargets.length === 0}
                                        onChecked={(checked) => toggleMany(pagedTargets, checked)}
                              />
                          </div>
                      )
                  }
                  <div className="w-24 flex-shrink-0">타입</div>
                  <div className="flex-1 min-w-0">진입점명</div>
                  <div className="w-28 flex-shrink-0">ID</div>
                  <div className="w-20 flex-shrink-0 text-center">상태</div>
                  {isSingle && <div className="w-[130px] flex-shrink-0 text-center"></div>}
              </div>

              {/* 행 */}
              {
                  paged.map((spot) => {
                      const connected = connectedIds.has(spot.id)
                      const willRemove = connected && removedIds.has(spot.id)
                      const checked = isChecked(spot)
                      return (
                          <div key={spot.id}
                               className={"flex items-center gap-4 px-4 py-3 border-b border-gray-100"
                                   + (willRemove ? " bg-red-50 text-gray-500"
                                       : connected ? " bg-gray-50 text-gray-500"
                                       : (checked ? " bg-blue-50" : ""))}
                          >
                              {
                                  isSingle === false && (
                                      <div className="w-6 flex-shrink-0">
                                          {/* 이미 연결된 것은 `allowDisconnect` 일 때만 체크를 풀 수 있다.
                                              (시작한 프로젝트에서는 종전처럼 고정 — 상세 화면의 X 버튼도 같은 조건으로 숨는다) */}
                                          <Checkbox checked={checked}
                                                    disabled={connected && !allowDisconnect}
                                                    onChecked={(next) => toggleMany([spot], next)}
                                          />
                                      </div>
                                  )
                              }
                              <div className="w-24 flex-shrink-0">
                                  <Tag color="info">{spotTypeLabel(spot.type)}</Tag>
                              </div>
                              <div className="flex-1 min-w-0">
                                  <div className="truncate">{spot.name}</div>
                                  {
                                      /* 🔴 다른 진행중·예약 프로젝트가 이 진입점을 쓰고 있다.
                                         고르는 것은 막지 않지만, 그대로 시작하면 **시작 시점에 막힌다**
                                         (`validationEntryPointAndGetName` — 기간이 겹치면 차단). */
                                      (occupancy[spot.id] ?? []).length > 0 && (
                                          <div className="text-xs text-amber-700 truncate">
                                              {occupancy[spot.id].map((o) =>
                                                  `${o.scheduled ? "예약" : "진행중"} · ${o.projectName} (${o.period})`
                                              ).join(" / ")}
                                          </div>
                                      )
                                  }
                              </div>
                              <div className="w-28 flex-shrink-0">
                                  <Tag>{spotShortId(spot.uid)}</Tag>
                              </div>
                              <div className="w-20 flex-shrink-0 text-center text-xs">
                                  {willRemove ? <Tag color="error">해제 예정</Tag>
                                      : connected ? <Tag color="success">연결됨</Tag>
                                      : (occupancy[spot.id] ?? []).length > 0 ? <Tag color="warning">점유 중</Tag>
                                      : ""}
                              </div>
                              {
                                  isSingle && (
                                      <div className="w-[130px] flex-shrink-0 text-center">
                                          <Button variants="dark" value="선택 연결"
                                                  disabled={connected}
                                                  onClick={() => onSelected({id: spot.id, name: spot.name})}
                                          />
                                      </div>
                                  )
                              }
                          </div>
                      )
                  })
              }

              {
                  paged.length === 0 && (
                      <div className="flex-center-all py-16 text-gray-400">
                          {loading
                              ? "진입점을 불러오는 중입니다..."
                              : connectedOnly
                                  ? "이 이벤트에 연결된 진입점이 없습니다. '연결된 것만 보기'를 끄면 전체 목록에서 고를 수 있습니다."
                                  : (keyword.trim() === ""
                                      ? "등록된 진입점이 없습니다."
                                      : "검색 결과가 없습니다.")}
                      </div>
                  )
              }
          </div>

          {/* flex-shrink-0 = 높이가 모자랄 때 스크롤 영역이 줄고 이 줄은 남는다(잘리면 페이지 이동이 불가) */}
          <div className="flex justify-center flex-shrink-0">
              <Pagination pageSize={PAGE_SIZE} totalCount={filteredCount}
                          currentPage={page} onPaging={(next) => setPage(next)}
              />
          </div>
      </div>
    )
}

export default EntrypointSimpleList
